import React, { useState, useEffect } from "react";
import { useGame } from "../context/GameContext";
import socketService from "../services/socketService";
import { Trophy, Dices, Check, StickyNote, X, Hourglass } from "lucide-react";

const AVATARS = [
  { id: 'renard', image: '/images/renard.png', name: 'Renard', color: '#FF6B35' },
  { id: 'loup', image: '/images/loup.png', name: 'Loup', color: '#4A5568' },
  { id: 'lion', image: '/images/lion.png', name: 'Lion', color: '#D69E2E' },
  { id: 'panda', image: '/images/panda.png', name: 'Panda', color: '#2D3748' },
  { id: 'hibou', image: '/images/hibou.png', name: 'Hibou', color: '#805AD5' },
  { id: 'dragon', image: '/images/dragon.png', name: 'Dragon', color: '#38A169' },
  { id: 'poulpe', image: '/images/poulpe.png', name: 'Poulpe', color: '#E53E3E' },
  { id: 'aigle', image: '/images/aigle.png', name: 'Aigle', color: '#2B6CB0' },
  { id: 'tigre', image: '/images/tigre.png', name: 'Tigre', color: '#DD6B20' },
  { id: 'papillon', image: '/images/papillon.png', name: 'Papillon', color: '#9F7AEA' },
  { id: 'chat', image: '/images/chat.png', name: 'Chat', color: '#F6AD55' },
  { id: 'cygne', image: '/images/cygne.png', name: 'Cygne', color: '#63B3ED' },
  { id: 'lapin', image: '/images/lapin.png', name: 'Lapin', color: '#FC8181' },
  { id: 'tortue', image: '/images/tortue.png', name: 'Tortue', color: '#68D391' },
];

const getAvatarById = (avatarId) => {
  return AVATARS.find((a) => a.id === avatarId) || null;
};

const Game = () => {
  const {
    roomId,
    gameState,
    players,
    visibleCharacters,
    hasSubmittedCharacter,
    submitCharacter,
    passTurn,
    guessCharacter,
    isMyTurn,
    getCurrentPlayer,
    getMyPlayer,
    voteToCancel,
    cancelVotes,
    theme,
    hintsEnabled,
    hintRequests,
    revealedHints,
    myHint,
    requestHint,
    isSpectator,
    turnNumber,
    turnOrder,
    lastWinner,
  } = useGame();

  const [characterInput, setCharacterInput] = useState("");
  const [hintInput, setHintInput] = useState("");
  const [guessInput, setGuessInput] = useState("");
  const [showGuessModal, setShowGuessModal] = useState(false);
  const [loadingRandomCharacter, setLoadingRandomCharacter] = useState(false);
  const [notepadOpen, setNotepadOpen] = useState(false);
  const [notepadContent, setNotepadContent] = useState(() => {
    return localStorage.getItem("notepad_" + (localStorage.getItem("roomId") || "")) || "";
  });

  useEffect(() => {
    socketService.enableAutoReconnect();
  }, []);

  // Persist notepad to localStorage
  useEffect(() => {
    if (roomId) {
      localStorage.setItem("notepad_" + roomId, notepadContent);
    }
  }, [notepadContent, roomId]);

  const handleSubmitCharacter = (e) => {
    e.preventDefault();
    if (characterInput.trim()) {
      submitCharacter(characterInput.trim(), hintInput.trim() || null);
      setCharacterInput("");
      setHintInput("");
    }
  };

  const handleRandomCharacter = async () => {
    setLoadingRandomCharacter(true);
    try {
      const response = await fetch("/personnages.json");
      const data = await response.json();
      const chars = data.characters || [];
      if (chars.length > 0) {
        setCharacterInput(chars[Math.floor(Math.random() * chars.length)]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération d'un personnage:", error);
    } finally {
      setLoadingRandomCharacter(false);
    }
  };

  const handlePassTurn = () => {
    passTurn();
  };

  const handleGuess = (e) => {
    e.preventDefault();
    if (guessInput.trim()) {
      guessCharacter(guessInput.trim());
      setGuessInput("");
      setShowGuessModal(false);
    }
  };

  const myPlayer = getMyPlayer();
  const currentPlayer = getCurrentPlayer();
  const myTurn = isMyTurn();
  const mySocketId = myPlayer?.id;
  const hasSubmitted = hasSubmittedCharacter.includes(mySocketId);

  // Dynamic page title
  useEffect(() => {
    if (gameState === "playing") {
      if (myTurn) {
        document.title = "(Votre tour!) Guess Party";
      } else {
        document.title = `Tour de ${currentPlayer?.pseudo || "..."} - Guess Party`;
      }
    } else if (gameState === "entering_characters") {
      document.title = "Choix du personnage - Guess Party";
    } else {
      document.title = "Guess Party";
    }
    return () => { document.title = "Guess Party"; };
  }, [gameState, myTurn, currentPlayer?.pseudo]);

  // Confetti on victory
  useEffect(() => {
    if (!lastWinner) return;

    const colors = ['#FF6B35', '#4F46E5', '#9333EA', '#10B981', '#F59E0B', '#EF4444'];
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;';
    document.body.appendChild(container);

    for (let i = 0; i < 60; i++) {
      const confetti = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const left = Math.random() * 100;
      const delay = Math.random() * 0.5;
      const size = Math.random() * 8 + 4;
      confetti.style.cssText = `position:absolute;top:-10px;left:${left}%;width:${size}px;height:${size}px;background:${color};border-radius:${Math.random() > 0.5 ? '50%' : '0'};animation:confetti-fall ${2 + Math.random()}s ease-in ${delay}s forwards;`;
      container.appendChild(confetti);
    }

    setTimeout(() => container.remove(), 3500);
  }, [lastWinner]);

  // Prevent accidental page close during a game
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (gameState === "playing" || gameState === "entering_characters") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [gameState]);

  // Phase de saisie des personnages
  if (gameState === "entering_characters") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full">
          <h1 className="text-3xl font-bold text-purple-600 mb-6 text-center">
            Choisissez un personnage
          </h1>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800">
              <span className="font-semibold">Règle :</span> Choisissez un
              personnage (réel ou fictif). Ce personnage sera attribué à un
              autre joueur qui devra le deviner.
            </p>
          </div>
          {theme && (
            <div className="mb-6 p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
              <span className="text-sm text-gray-600">Thème : </span>
              <span className="font-bold text-purple-800">{theme}</span>
            </div>
          )}
          {isSpectator ? (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <p className="text-blue-800 font-semibold">Vous êtes spectateur</p>
              <p className="text-sm text-blue-600 mt-1">En attente que les joueurs choisissent leurs personnages...</p>
            </div>
          ) : !hasSubmitted ? (
            <form onSubmit={handleSubmitCharacter} className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">
                Nom du personnage :
              </label>
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Ex: Harry Potter, Emmanuel Macron..."
                  value={characterInput}
                  onChange={(e) => setCharacterInput(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleRandomCharacter}
                  disabled={loadingRandomCharacter}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-purple-600 transition duration-200 disabled:opacity-50"
                  title="Générer un personnage aléatoire"
                >
                  {loadingRandomCharacter ? <Hourglass size={18} className="animate-spin" /> : <Dices size={18} />}
                </button>
              </div>
              {hintsEnabled && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Indice (optionnel) :
                  </label>
                  <input
                    type="text"
                    placeholder="Un indice pour aider à deviner..."
                    value={hintInput}
                    onChange={(e) => setHintInput(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    maxLength={100}
                  />
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
              >
                Valider mon personnage
              </button>
            </form>
          ) : (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-green-800 font-semibold">
                <Check size={16} className="inline mr-1" /> Vous avez soumis votre personnage
              </p>
              <p className="text-gray-600 text-sm mt-2">
                En attente des autres joueurs...
              </p>
            </div>
          )}
          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Joueurs ({hasSubmittedCharacter.length}/{players.length})
            </h2>
            <div className="space-y-2">
              {players.map((player) => {
                const submitted = hasSubmittedCharacter.includes(player.id);
                return (
                  <div
                    key={player.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      submitted
                        ? "bg-green-50 border border-green-200"
                        : "bg-gray-50 border border-gray-200"
                    }`}
                  >
                    <span className="font-semibold text-gray-800">
                      {player.pseudo}
                    </span>
                    {submitted && (
                      <span className="text-green-600 font-semibold"><Check size={16} /></span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-6 border-t pt-6">
            <button
              onClick={voteToCancel}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Voter pour annuler la partie
            </button>
            {cancelVotes.votes > 0 && (
              <div className="mt-2 text-center text-sm text-gray-600">
                {cancelVotes.votes}/{cancelVotes.total} vote(s) -{" "}
                {cancelVotes.required} requis pour annuler
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Phase de jeu
  if (gameState === "playing") {
    return (
      <div className="min-h-screen flex flex-col items-center p-2 sm:p-4 animate-fade-in">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">
              <span style={{ color: "#4F46E5" }}>Guess</span>{" "}
              <span style={{ color: "#9333EA" }}>Party</span>
            </h1>
            <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
              <span className="font-bold text-white">{roomId}</span>
            </div>
          </div>

          {/* Spectator banner */}
          {isSpectator && (
            <div className="mb-3 p-2 bg-blue-500 bg-opacity-20 border border-blue-400 rounded-xl text-center">
              <span className="text-blue-200 font-medium">Mode spectateur</span>
            </div>
          )}

          {/* Turn indicator */}
          <div
            className="mb-3 p-2 rounded-xl text-center"
            style={{
              background: myTurn
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            }}
          >
            {myTurn ? (
              <p className="text-white text-xl font-bold animate-float">
                C'est votre tour !
              </p>
            ) : (
              <p className="text-white text-lg font-semibold">
                Tour de : {currentPlayer?.pseudo || "..."}
              </p>
            )}
            {turnNumber > 0 && (
              <p className="text-white text-opacity-60 text-xs">Tour {turnNumber}</p>
            )}
          </div>

          {/* Player cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(() => {
              const sortedPlayers = [...players].sort((a, b) => {
                const aIdx = turnOrder.indexOf(a.id);
                const bIdx = turnOrder.indexOf(b.id);
                if (aIdx === -1 && bIdx === -1) return 0;
                if (aIdx === -1) return 1;
                if (bIdx === -1) return -1;
                return aIdx - bIdx;
              });
              return sortedPlayers;
            })().map((player) => {
              const avatar = getAvatarById(player.avatar);
              const isCurrentTurn = player.id === currentPlayer?.id;
              const isMe = player.id === mySocketId;

              const charInfo = visibleCharacters.find(
                (c) => c.playerId === player.id
              );

              let characterText;
              if (isSpectator) {
                characterText = charInfo ? charInfo.character : "...";
              } else if (isMe) {
                characterText = player.hasWon ? charInfo?.character || "???" : "???";
              } else if (charInfo) {
                characterText = charInfo.character;
              } else {
                characterText = "...";
              }

              return (
                <div
                  key={player.id}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${
                    isCurrentTurn ? "animate-pulse-glow" : ""
                  }`}
                  style={{
                    backgroundColor: player.hasWon
                      ? "rgba(16, 185, 129, 0.2)"
                      : isCurrentTurn
                        ? "rgba(99, 102, 241, 0.25)"
                        : "rgba(255, 255, 255, 0.1)",
                    border: isCurrentTurn
                      ? "2px solid rgba(199, 210, 254, 0.8)"
                      : player.hasWon
                        ? "2px solid rgba(16, 185, 129, 0.4)"
                        : "2px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="rounded-full overflow-hidden"
                      style={{
                        width: 64,
                        height: 64,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      {avatar ? (
                        <img
                          src={avatar.image}
                          alt={avatar.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-400 text-white text-xl">?</div>
                      )}
                      {player.hasWon && (
                        <div
                          className="absolute inset-0 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: "rgba(16, 185, 129, 0.7)" }}
                        >
                          <span className="text-white font-bold text-sm">
                            #{player.winPosition}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Online indicator */}
                    <div
                      className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2"
                      style={{
                        backgroundColor: player.isOnline !== false ? "#22c55e" : "#ef4444",
                        borderColor: "rgba(99, 102, 241, 0.25)",
                      }}
                    />
                  </div>

                  {/* Pseudo + Character */}
                  <div className="flex flex-col min-w-0">
                    <span className="font-bold text-white text-lg truncate">
                      {player.pseudo}
                      {player.hasWon && <Trophy size={14} className="inline ml-1" />}
                      {isMe && <span className="text-indigo-300 font-normal text-sm"> (vous)</span>}
                    </span>
                    <span
                      className={`text-base truncate ${
                        isMe ? "text-indigo-300 italic" : "text-gray-300"
                      }`}
                    >
                      {characterText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          {!isSpectator && myTurn && !myPlayer?.hasWon && (
            <div className="flex gap-3 justify-center mt-3">
              <button
                onClick={handlePassTurn}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2.5 px-6 rounded-xl transition duration-200"
              >
                Passer mon tour
              </button>
              <button
                onClick={() => setShowGuessModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-xl transition duration-200 transform hover:scale-105"
              >
                Deviner mon personnage
              </button>
            </div>
          )}

          {/* Hint system - disponible à partir du tour 10 */}
          {!isSpectator && !myPlayer?.hasWon && (
            <div className="mt-3 text-center">
              {myHint ? (
                <div className="p-2 bg-indigo-500 bg-opacity-20 border border-indigo-400 rounded-xl">
                  <span className="text-indigo-200">Votre indice : </span>
                  <span className="text-white font-bold text-lg">{myHint}</span>
                </div>
              ) : revealedHints.includes(mySocketId) ? (
                <div className="p-2 bg-gray-500 bg-opacity-20 rounded-xl">
                  <span className="text-gray-300 text-sm">Indice déjà révélé</span>
                </div>
              ) : hintRequests.includes(mySocketId) ? (
                <div className="p-2 bg-yellow-500 bg-opacity-20 border border-yellow-400 rounded-xl">
                  <span className="text-yellow-200">En attente d'approbation...</span>
                </div>
              ) : turnNumber >= 10 ? (
                <button
                  onClick={requestHint}
                  className="bg-indigo-500 bg-opacity-80 hover:bg-indigo-600 text-white font-semibold py-2 px-5 rounded-xl transition duration-200"
                >
                  Demander mon indice
                </button>
              ) : null}
            </div>
          )}



          {myPlayer?.hasWon && (
            <div className="mt-3">
              <div className="p-3 bg-green-500 bg-opacity-20 border border-green-400 rounded-xl text-center">
                <p className="text-white font-semibold text-lg">
                  <Trophy size={14} className="inline mr-1" /> Vous avez gagné ! Position : #{myPlayer.winPosition}
                </p>
                <p className="text-green-200 text-sm mt-1">
                  En attente de la fin de la partie...
                </p>
              </div>
            </div>
          )}

          {/* Cancel vote - compact */}
          {!isSpectator && <div className="mt-3 text-center">
            <button
              onClick={voteToCancel}
              className="bg-red-500 bg-opacity-60 hover:bg-red-600 text-white font-medium py-1.5 px-4 rounded-xl transition duration-200 text-sm"
            >
              Voter pour annuler
            </button>
            {cancelVotes.votes > 0 && (
              <span className="ml-2 text-xs text-white text-opacity-60">
                {cancelVotes.votes}/{cancelVotes.total} ({cancelVotes.required} requis)
              </span>
            )}
          </div>}
        </div>

        {/* Notepad - Desktop: side panel, Mobile: floating button + bottom panel */}
        {/* Desktop side panel */}
        <div className="hidden lg:block fixed right-4 top-32 w-64">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-3">
            <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
              <StickyNote size={14} /> Bloc-notes
            </h3>
            <textarea
              value={notepadContent}
              onChange={(e) => setNotepadContent(e.target.value)}
              placeholder="Notez vos indices ici..."
              className="w-full h-48 bg-white bg-opacity-10 text-white placeholder-white placeholder-opacity-40 text-sm rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-purple-400 border border-white border-opacity-10"
            />
          </div>
        </div>

        {/* Mobile floating button */}
        <button
          onClick={() => setNotepadOpen(!notepadOpen)}
          className="lg:hidden fixed bottom-4 right-4 w-12 h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg flex items-center justify-center text-xl transition duration-200 z-40"
        >
          <StickyNote size={20} />
        </button>

        {/* Mobile bottom panel */}
        {notepadOpen && (
          <div className="lg:hidden fixed bottom-20 right-4 left-4 z-40">
            <div className="bg-white rounded-xl shadow-2xl p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <StickyNote size={14} /> Bloc-notes
                </h3>
                <button
                  onClick={() => setNotepadOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg"
                >
                  <X size={18} />
                </button>
              </div>
              <textarea
                value={notepadContent}
                onChange={(e) => setNotepadContent(e.target.value)}
                placeholder="Notez vos indices ici..."
                className="w-full h-32 bg-gray-50 text-gray-800 placeholder-gray-400 text-sm rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-200"
              />
            </div>
          </div>
        )}

        {/* Guess modal */}
        {showGuessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-purple-600 mb-4">
                Qui êtes-vous ?
              </h2>
              <form onSubmit={handleGuess}>
                <label className="block text-gray-700 font-semibold mb-2">
                  Entrez le nom de votre personnage :
                </label>
                <input
                  type="text"
                  placeholder="Nom du personnage"
                  value={guessInput}
                  onChange={(e) => setGuessInput(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowGuessModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Valider
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-lg shadow-2xl p-8">
        <p className="text-gray-600">Chargement...</p>
      </div>
    </div>
  );
};

export default Game;
